import { useEffect, useRef, useState } from "react";
import type { ProductData } from "@/mocks/products";

interface Props {
  product: ProductData;
}

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function ProductTransformation({ product }: Props) {
  const { ref, inView } = useInView(0.1);

  return (
    <section className="relative bg-[#060508] py-40 px-6 overflow-hidden">
      {/* Center glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[300px] pointer-events-none"
        style={{
          background: `radial-gradient(ellipse, ${product.glowColor} 0%, transparent 70%)`,
          filter: "blur(60px)",
        }}
      />

      <div ref={ref} className="max-w-4xl mx-auto text-center">
        <div
          style={{
            opacity: inView ? 1 : 0,
            transition: "opacity 1.5s ease",
          }}
        >
          <div
            className="text-xs tracking-[0.5em] uppercase mb-8"
            style={{ color: `${product.accentColor}50` }}
          >
            Transformation
          </div>
          <h2
            className="text-3xl md:text-4xl font-light text-white/70 mb-20 leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: "0.04em" }}
          >
            変容の地図
          </h2>
        </div>

        <div className="space-y-12">
          {product.transformations.map((t, i) => (
            <div
              key={i}
              className="flex items-center justify-center gap-8 md:gap-16"
              style={{
                opacity: inView ? 1 : 0,
                transform: inView ? "translateY(0)" : "translateY(20px)",
                transition: `opacity 1.8s ease ${0.3 + i * 0.2}s, transform 1.8s ease ${0.3 + i * 0.2}s`,
              }}
            >
              {/* From */}
              <div className="text-right min-w-[120px]">
                <span
                  className="text-2xl md:text-3xl font-light text-white/20"
                  style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: "0.05em" }}
                >
                  {t.from}
                </span>
              </div>

              {/* Arrow */}
              <div className="flex items-center gap-3 flex-shrink-0">
                <div className="w-8 h-px" style={{ background: `${product.accentColor}30` }} />
                <div
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: `${product.accentColor}60` }}
                />
                <div className="w-8 h-px" style={{ background: `${product.accentColor}30` }} />
              </div>

              {/* To */}
              <div className="text-left min-w-[120px]">
                <span
                  className="text-2xl md:text-3xl font-light"
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    letterSpacing: "0.05em",
                    color: product.accentColor,
                    textShadow: `0 0 30px ${product.glowColor}`,
                  }}
                >
                  {t.to}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom ornament */}
        <div
          className="mt-20 flex items-center justify-center gap-4"
          style={{
            opacity: inView ? 0.3 : 0,
            transition: "opacity 2s ease 1s",
          }}
        >
          <div className="w-16 h-px" style={{ background: `${product.accentColor}40` }} />
          <div
            className="w-1 h-1 rounded-full"
            style={{ background: `${product.accentColor}60` }}
          />
          <div className="w-16 h-px" style={{ background: `${product.accentColor}40` }} />
        </div>
      </div>
    </section>
  );
}
